// A custom hook for tracking changes to the activity log
import { useCallback } from 'react';
import axiosInstance from '../axios/axiosConfig';
import { useSelector } from 'react-redux';

/**
 * Hook for logging changes to the activity log
 * @returns {Function} logActivityLogChange - Function to log activity log changes
 */
const useActivityLogChangeLog = () => {
  // Get API URLs from Redux store
  const apiUrls = useSelector((state) => state.apiUrls.urls);
  
  // Get user details from Redux store for audit logging
  const userDetails = useSelector((state) => state.user.userDetails);
  const userRoles = useSelector((state) => state.user.userRoles);
  const currentUser = userDetails?.name || userDetails?.displayName || "Unknown";
  const primaryRole = userRoles?.length > 0 ? userRoles[0] : "User";

  /**
   * Log a change to the activity log
   * @param {number} recordId - The ID of the activity log record that was changed
   * @param {string} projectName - The project name of the activity log record
   * @param {string} jobNumber - The job number (if applicable)
   * @param {string} field - The field that was changed
   * @param {string} prevValue - The previous value of the field
   * @param {string} newValue - The new value of the field
   * @returns {Promise} - The result of the API call
   */
  const logActivityLogChange = useCallback(async (recordId, projectName, jobNumber, field, prevValue, newValue) => {
    try {
      if (!apiUrls.activityLogChangeLog) {
        console.error('activityLogChangeLog API URL is not defined');
        return;
      }

      const changeData = {
        RecordID: recordId,
        ProjectName: projectName || '',
        JobNumber: jobNumber || '',
        CurrentUser: currentUser,
        UserRole: primaryRole,
        Field: field,
        PrevValue: prevValue || '',
        NewValue: newValue || '',
        DateTime: new Date().toISOString()
      };

      // Make the API call to log the change
      const response = await axiosInstance.post(apiUrls.activityLogChangeLog, changeData);
      return response.data;
    } catch (error) {
      console.error('Error logging activity log change:', error);
      // We don't want to block the UI if logging fails, so we just log the error
    }
  }, [apiUrls.activityLogChangeLog, currentUser, primaryRole]);

  return { logActivityLogChange };
};

export default useActivityLogChangeLog;
